// Working-day maths for lead times: Mon-Fri, excluding South African public holidays.
// Easter-linked holidays are computed per year so this keeps working past 2026.

const FIXED_HOLIDAYS = [
  [1, 1], // New Year's Day
  [3, 21], // Human Rights Day
  [4, 27], // Freedom Day
  [5, 1], // Workers' Day
  [6, 16], // Youth Day
  [8, 9], // National Women's Day
  [9, 24], // Heritage Day
  [12, 16], // Day of Reconciliation
  [12, 25], // Christmas Day
  [12, 26], // Day of Goodwill (Boxing Day)
];

const iso = (date) => date.toISOString().split('T')[0];

// Anonymous Gregorian algorithm
const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
};

const cache = new Map();

export const saPublicHolidays = (year) => {
  if (cache.has(year)) return cache.get(year);

  const dates = FIXED_HOLIDAYS.map(([month, day]) => new Date(Date.UTC(year, month - 1, day)));
  const easter = easterSunday(year);
  const goodFriday = new Date(easter);
  goodFriday.setUTCDate(easter.getUTCDate() - 2);
  const familyDay = new Date(easter); // Easter Monday / Family Day
  familyDay.setUTCDate(easter.getUTCDate() + 1);
  dates.push(goodFriday, familyDay);

  // SA rule: a public holiday falling on a Sunday is observed on the Monday
  const observed = new Set(dates.map(iso));
  dates.forEach((date) => {
    if (date.getUTCDay() === 0) {
      const monday = new Date(date);
      monday.setUTCDate(date.getUTCDate() + 1);
      observed.add(iso(monday));
    }
  });

  cache.set(year, observed);
  return observed;
};

export const isWorkingDay = (date) => {
  const day = date.getUTCDay();
  if (day === 0 || day === 6) return false;
  return !saPublicHolidays(date.getUTCFullYear()).has(iso(date));
};

/**
 * Working days elapsed between two dates (start excluded, end included).
 * Same-day => 0. Returns null when either date is missing or the range is reversed.
 */
export const calculateWorkingDays = (fromDate, toDate) => {
  if (!fromDate || !toDate) return null;

  const from = new Date(`${String(fromDate).split('T')[0]}T00:00:00Z`);
  const to = new Date(`${String(toDate).split('T')[0]}T00:00:00Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) return null;

  let days = 0;
  const cursor = new Date(from);
  while (cursor < to) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    if (isWorkingDay(cursor)) days += 1;
  }
  return days;
};

export const formatWorkingDays = (days) => {
  if (days === null || days === undefined) return '';
  return `${days} working day${days === 1 ? '' : 's'}`;
};
